import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { StarRating, StockBadge, PriceTag } from "@/components/ui-bits";
import { useLang } from "@/lib/i18n";

const EASE = [0.16, 1, 0.3, 1];

export const ProductCard = ({ product, index = 0 }) => {
  const navigate = useNavigate();
  const { add } = useCart();
  const { t, pick } = useLang();
  const style = product.image_style || "atelier";
  const boxed = style === "studio" || style === "promo";
  const noPrice = !product.price || product.price <= 0;
  const soldOut = (product.stock ?? 0) <= 0;

  const frameBg = style === "studio" ? "bg-[#ECEAE3]" : "bg-ink";
  const imgClass = style === "studio" ? "object-contain p-6 sm:p-8" : style === "promo" ? "object-contain p-3" : "object-cover";

  return (
    <motion.article
      data-testid={`product-card-${product.slug}`}
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.06, ease: EASE }}
      whileHover={{ y: -6 }}
      className="group cursor-pointer"
      onClick={() => navigate(`/product/${product.slug}`)}
    >
      <div className="product-frame relative border border-gold/10 bg-ink">
        <div className={`aspect-[3/4] w-full ${frameBg}`}>
          <img
            src={product.image_url}
            alt={product.model_fr || product.model}
            className={`w-full h-full transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.04] ${imgClass}`}
          />
        </div>
        {style === "atelier" && (
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-obsidian/70 to-transparent pointer-events-none" />
        )}
        <span className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.3em] text-gold/90 bg-obsidian/60 backdrop-blur-sm px-3 py-1.5 border border-gold/20 uppercase">
          {product.brand}
        </span>
        {soldOut && (
          <div className="absolute inset-0 flex items-center justify-center bg-obsidian/70 backdrop-blur-[2px]">
            <span className="font-serif italic text-cream text-2xl">{t("shop.soldOut")}</span>
          </div>
        )}
        {!soldOut && !noPrice && (
          <button
            data-testid={`quick-add-${product.slug}`}
            onClick={(e) => {
              e.stopPropagation();
              add(product);
            }}
            aria-label={t("product.add")}
            className="absolute bottom-4 right-4 flex items-center gap-2 bg-gold text-obsidian font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 hover:bg-gold-light"
          >
            <ShoppingBag size={12} /> {t("product.add")}
          </button>
        )}
      </div>
      <div className="pt-5">
        <div className="flex items-baseline justify-between gap-4">
          <h3 className="font-serif text-lg text-cream leading-snug">{pick(product, "model")}</h3>
          <span className="font-mono text-[10px] tracking-[0.2em] text-smoke whitespace-nowrap">{product.ml}</span>
        </div>
        <div className="mt-2 flex items-center justify-between gap-3">
          <StarRating value={product.rating_avg || 0} count={product.rating_count || 0} />
          <StockBadge stock={product.stock ?? 0} />
        </div>
        <div className="mt-3 flex items-center justify-between border-t border-gold/10 pt-3">
          <PriceTag price={product.price} discountPrice={product.discount_price} />
        </div>
      </div>
    </motion.article>
  );
};

export default ProductCard;
